import { useState, type MouseEvent } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AppBar, Box, Drawer, IconButton, Toolbar } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../contexts/AuthContext';
import { isLandingPath, scrollToMarketingHash } from '../../marketing/marketingHash';
import { marketingNav } from '../../marketing/marketingNav';
import MarketingNavLink from './MarketingNavLink';
import MarketingThemeToggle from './MarketingThemeToggle';

const L = `${process.env.PUBLIC_URL || ''}/marketing-assets`;
const LOGO = `${L}/VisiMe_Logo_RGB-1.png`;

type MarketingHeaderProps = {
  variant: 'hero' | 'solid';
  onHero?: boolean;
};

export default function MarketingHeader({ variant, onHero }: MarketingHeaderProps) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const transparent = variant === 'hero' && onHero !== false;
  const headerClass = transparent ? 'landing-header' : variant === 'hero' ? 'landing-header landing-header--scrolled' : 'landing-header landing-header--solid';

  const goToDownload = (e: MouseEvent) => {
    setDrawer(false);
    if (isLandingPath(location.pathname)) {
      e.preventDefault();
      window.history.replaceState(null, '', '/landing#download');
      scrollToMarketingHash('#download');
    }
  };

  const downloadCta = (
    <Box component={RouterLink} to="/landing#download" onClick={goToDownload} className="landing-cta-primary landing-header-download">
      App laden
    </Box>
  );

  const loginLink = isAuthenticated ? (
    <Box component={RouterLink} to="/homepage" className="landing-header-login">
      Zur Plattform
    </Box>
  ) : (
    <Box component={RouterLink} to="/?login=1" className="landing-header-login">
      Anmelden
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0} className={headerClass}>
        <Toolbar disableGutters className="landing-marketing-toolbar">
          <Box component={RouterLink} to="/landing" className="landing-header-logo" aria-label="VisiMe Startseite">
            <Box component="img" src={LOGO} alt="" />
            <span className="landing-header-wordmark">VisiMe</span>
          </Box>
          <Box className="landing-header-nav-slot">
            <nav className="landing-header-nav" aria-label="Hauptnavigation">
              {marketingNav.map((item) => (
                <MarketingNavLink key={item.labelKey} item={item} />
              ))}
            </nav>
          </Box>
          <Box className="landing-header-cta-slot">
            <Box className="landing-header-actions">
              <MarketingThemeToggle />
              {loginLink}
              {downloadCta}
            </Box>
          </Box>
          <Box className="landing-header-tools">
            <MarketingThemeToggle />
            <IconButton className="landing-header-menu-btn" onClick={() => setDrawer(true)} aria-label="Menü öffnen">
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)}>
        <Box className="landing-drawer landing-page">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <span className="landing-header-wordmark">VisiMe</span>
            <IconButton onClick={() => setDrawer(false)} aria-label="Schliessen">
              <CloseIcon />
            </IconButton>
          </Box>
          <nav aria-label="Hauptnavigation mobil">
            {marketingNav.map((item) => (
              <MarketingNavLink key={item.labelKey} item={item} onNavigate={() => setDrawer(false)} />
            ))}
          </nav>
          <Box className="landing-drawer-cta">
            {isAuthenticated ? (
              <Box component={RouterLink} to="/homepage" className="landing-cta-secondary" onClick={() => setDrawer(false)}>
                Zur Plattform
              </Box>
            ) : (
              <Box component={RouterLink} to="/?login=1" className="landing-cta-secondary" onClick={() => setDrawer(false)}>
                Anmelden
              </Box>
            )}
            <Box component={RouterLink} to="/landing#download" onClick={goToDownload} className="landing-cta-primary">
              App laden
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
